import { useState, useMemo, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
    AlertTriangle, Users, BookOpen, Target, Shield,
    ChevronDown, ChevronUp, Download, Loader2, GraduationCap, Award,
    CheckCircle, CircleDashed, CalendarOff, TrendingUp, Hourglass,
    ExternalLink,
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
    learnerReportService,
    LearnerReportType,
    UrgencyLevel,
    TimelineStatus,
    LearnerTrackingStatus,
    LearnerActivityReportResponse,
} from '@/services/learner-report.service';
import { toast } from '@/components/ui/Toast';
import Pagination from '@/components/ui/Pagination';
import Modal from '@/components/ui/Modal';
import ExcelJS from 'exceljs';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { formatDate } from '@/utils/dateFormat';
import { SkeletonLoader, MetricCardSkeleton, TableSkeleton } from '@/components/ui/SkeletonLoader';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { TRAINING_STATUS_REPORT_PERMISSION } from '@/constants/constant-permissions';

// ── Reference report colors (matches the HTML report exactly) ──────
const PRIMARY_PURPLE = '#534AB7';
const DARK_PURPLE = '#3d3790';
const SUCCESS_GREEN = '#1D9E75';
const DANGER_RED = '#C4432B';
const WARNING_AMBER = '#D4A843';
const INFO_BLUE = '#2B7AB5';

// ── Color / label maps ───────────────────────────────────────────
const URGENCY_COLOR: Record<UrgencyLevel, string> = {
    CRITICAL: DANGER_RED, HIGH: '#E06D44', MEDIUM: WARNING_AMBER, LOW: INFO_BLUE,
};
const TIMELINE_LABEL: Record<TimelineStatus, string> = {
    COMPLETED: 'Completed', OVERDUE: 'Overdue', DUE_SOON: 'Due Soon', ON_TRACK: 'On Track', NO_DEADLINE: 'No Deadline',
};
const TRACKING_LABEL: Record<LearnerTrackingStatus, string> = {
    NOT_STARTED: 'Not Started', IN_PROGRESS: 'In Progress', COMPLETED: 'Completed',
};
const TYPE_LABEL: Record<string, string> = {
    COURSE: 'Course', LEARNING_PATH: 'Learning Path', CERTIFICATION: 'Certification',
};

// ── Format helpers ───────────────────────────────────────────────
/** Convert minutes → "HH:MM:SS". Seconds are always 00 since the API only exposes minute precision. */
function formatMinutesAsTime(totalMinutes: number | undefined): string {
    const m = totalMinutes || 0;
    const hh = Math.floor(m / 60);
    const mm = m % 60;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
}

// ── Tooltip ────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
        return (
            <div className="bg-gray-900 text-white p-3 rounded-lg shadow-lg border border-gray-700">
                <p className="font-medium mb-1">{label}</p>
                {payload.map((entry: any, i: number) => (
                    <p key={i} className="text-sm" style={{ color: entry.color }}>{entry.name}: {entry.value}</p>
                ))}
            </div>
        );
    }
    return null;
};

// ── Main Component ─────────────────────────────────────────────────
export default function TrainingStatusReport() {
    usePermissionRouteAccess([
        TRAINING_STATUS_REPORT_PERMISSION.ADMIN_TRAINING_STATUS_REPORT_READ,
        TRAINING_STATUS_REPORT_PERMISSION.ADMIN_TRAINING_STATUS_REPORT_WRITE,
    ]);
    const canWrite = useHasPermission([TRAINING_STATUS_REPORT_PERMISSION.ADMIN_TRAINING_STATUS_REPORT_WRITE]);

    // ── Filters ──
    const [activeTab, setActiveTab] = useState<LearnerReportType>('ALL');
    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounce(searchInput, DEFAULT_DEBOUNCE_DELAY);
    const [timelineFilter, setTimelineFilter] = useState<TimelineStatus | ''>('');
    const [trackingFilter, setTrackingFilter] = useState<LearnerTrackingStatus | ''>('');
    const [urgencyFilter, setUrgencyFilter] = useState<UrgencyLevel | ''>('');
    const [mandatoryFilter, setMandatoryFilter] = useState(false);

    // ── Pagination ──
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // ── Sort (client-side on current page) ──
    const [sortField, setSortField] = useState<'days_overdue' | 'days_left' | 'progress_percentage' | 'user_name' | 'deadline_date' | 'completed_at'>('days_overdue');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    // ── UI state ──
    const [isExporting, setIsExporting] = useState(false);
    const [cellDetailModal, setCellDetailModal] = useState<{ title: string; content: string } | null>(null);
    // Tracks "the next refetch was triggered by a card click" — drives full-page
    // skeleton (vs table-only skeleton for search/sort/page/page-size/tab changes).
    const [cardFilterPending, setCardFilterPending] = useState(false);

    const resetPage = () => setPage(1);

    // ── Card click handlers ───────────────────────────────
    // Top 4 cards are mutually exclusive — only one of Completed / Not Started
    // / In Progress / No Deadline can be active at a time. Top 4 + bottom 4
    // (urgency) can still combine. "No Deadline" stays mutually exclusive with
    // urgency (timeline NO_DEADLINE vs OVERDUE contradict). Each click sets
    // `cardFilterPending` so the next refetch shows the full-page skeleton.
    const applyStatusCard = (status: LearnerTrackingStatus) => {
        setCardFilterPending(true);
        if (trackingFilter === status) {
            setTrackingFilter('');
        } else {
            setTrackingFilter(status);
            if (timelineFilter === 'NO_DEADLINE') setTimelineFilter('');
        }
        resetPage();
    };
    const applyNoDeadlineCard = () => {
        setCardFilterPending(true);
        if (timelineFilter === 'NO_DEADLINE') {
            setTimelineFilter('');
        } else {
            setTimelineFilter('NO_DEADLINE');
            setTrackingFilter('');
            setUrgencyFilter('');
        }
        resetPage();
    };
    const applyUrgencyCard = (level: UrgencyLevel) => {
        setCardFilterPending(true);
        if (urgencyFilter === level) {
            setUrgencyFilter('');
            if (timelineFilter === 'OVERDUE') setTimelineFilter('');
        } else {
            setUrgencyFilter(level);
            setTimelineFilter('OVERDUE');
        }
        resetPage();
    };
    const clearCardFilters = () => {
        setCardFilterPending(true);
        setTrackingFilter('');
        setTimelineFilter('');
        setUrgencyFilter('');
        resetPage();
    };

    // ── Query 1: Summary (drives cards + charts, unfiltered by table filters) ──
    const { data: summaryData } = useQuery<LearnerActivityReportResponse>({
        queryKey: ['learner-report-summary', activeTab],
        queryFn: () => learnerReportService.getSummary(activeTab),
        placeholderData: keepPreviousData,
        refetchInterval: 5 * 60 * 1000,
        refetchIntervalInBackground: false,
    });

    // ── Query 2: Generate (drives table, with all filters) ──
    // `placeholderData: keepPreviousData` keeps the previous table visible while a new
    // fetch is in flight (after a filter change), so only the small inline loader fires —
    // the page never collapses to the full skeleton on subsequent interactions.
    const { data: reportData, isLoading: reportLoading, isFetching: reportFetching, isError, refetch } = useQuery<LearnerActivityReportResponse>({
        queryKey: ['learner-report', activeTab, debouncedSearch, timelineFilter, trackingFilter, urgencyFilter, mandatoryFilter, page, pageSize],
        queryFn: () => learnerReportService.generateReport({
            report_type: activeTab,
            search: debouncedSearch || undefined,
            timeline_status: timelineFilter || undefined,
            tracking_status: trackingFilter || undefined,
            urgency_levels: urgencyFilter ? [urgencyFilter] : undefined,
            mandatory_only: mandatoryFilter || undefined,
            page,
            limit: pageSize,
        }),
        placeholderData: keepPreviousData,
        refetchInterval: 5 * 60 * 1000,
        refetchIntervalInBackground: false,
    });

    // ── Query 3: Same filters as Query 2 but WITHOUT urgency — drives the
    // bottom 4 urgency cards so picking a specific urgency level doesn't
    // collapse the other three card values. Tracking/timeline/search/mandatory
    // changes still flow through. Only fires when urgency is active; otherwise
    // we reuse `summary` directly.
    const { data: baseReportData } = useQuery<LearnerActivityReportResponse>({
        queryKey: ['learner-report-base', activeTab, debouncedSearch, timelineFilter, trackingFilter, mandatoryFilter],
        queryFn: () => learnerReportService.generateReport({
            report_type: activeTab,
            search: debouncedSearch || undefined,
            timeline_status: timelineFilter || undefined,
            tracking_status: trackingFilter || undefined,
            mandatory_only: mandatoryFilter || undefined,
            page: 1,
            limit: 1,
        }),
        placeholderData: keepPreviousData,
        enabled: !!urgencyFilter,
    });

    // Prefer the generate endpoint's summary/breakdowns once loaded so cards
    // and charts reflect the active filters (matching export behavior).
    // Fall back to the summary endpoint's data for the very first paint,
    // before generate resolves.
    const summary = reportData?.summary || summaryData?.summary;
    const byDepartment = reportData?.by_department || summaryData?.by_department || [];
    const byItem = reportData?.by_item || summaryData?.by_item || [];

    // Top 4 status cards always reflect unfiltered totals from the summary
    // endpoint — they should not change when table filters are applied.
    const statusCardSummary = summaryData?.summary;

    // Bottom 4 urgency cards bind to this — when urgency is active, read
    // from the no-urgency query so sibling card values stay stable.
    const baseSummary = urgencyFilter ? (baseReportData?.summary || summary) : summary;

    // Full-page skeleton on first load OR when a card filter was just clicked.
    const isLoading = (reportLoading && !reportData) || (cardFilterPending && reportFetching);
    // Table-only skeleton for everything else (search, sort, page, page-size, tab change).
    const isTableRefreshing = reportFetching && !!reportData && !cardFilterPending;

    // Clear the card-pending flag once the fetch resolves so the next non-card
    // change goes back to the table-only skeleton.
    useEffect(() => {
        if (cardFilterPending && !reportFetching) {
            setCardFilterPending(false);
        }
    }, [cardFilterPending, reportFetching]);
    const records = reportData?.records || [];
    const pagination = reportData?.pagination;

    // ── Sorted current page ──
    const sortedRecords = useMemo(() => {
        const arr = [...records];
        arr.sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case 'days_overdue': cmp = a.days_overdue - b.days_overdue; break;
                case 'days_left': cmp = (a.days_left ?? 99999) - (b.days_left ?? 99999); break;
                case 'progress_percentage': cmp = a.progress_percentage - b.progress_percentage; break;
                case 'user_name': cmp = a.user_name.localeCompare(b.user_name); break;
                case 'deadline_date':
                    cmp = (a.deadline_date ? new Date(a.deadline_date).getTime() : Infinity)
                        - (b.deadline_date ? new Date(b.deadline_date).getTime() : Infinity);
                    break;
                case 'completed_at':
                    cmp = (a.completed_at ? new Date(a.completed_at).getTime() : 0)
                        - (b.completed_at ? new Date(b.completed_at).getTime() : 0);
                    break;
            }
            return sortOrder === 'desc' ? -cmp : cmp;
        });
        return arr;
    }, [records, sortField, sortOrder]);

    const handleSort = (field: typeof sortField) => {
        if (sortField === field) setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        else { setSortField(field); setSortOrder('desc'); }
    };
    const SortIcon = ({ field }: { field: typeof sortField }) => {
        if (sortField !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
        return sortOrder === 'desc' ? <ChevronDown className="w-3 h-3 text-indigo-500" /> : <ChevronUp className="w-3 h-3 text-indigo-500" />;
    };

    // ── Charts data ──
    const trackingChartData = useMemo(() => {
        if (!summary) return [];
        return [
            { name: 'Not Started', value: summary.not_started_count, color: DANGER_RED },
            { name: 'In Progress', value: summary.in_progress_count, color: WARNING_AMBER },
            { name: 'Completed', value: summary.completed_count, color: SUCCESS_GREEN },
            { name: 'Due Soon', value: summary.due_soon_count, color: INFO_BLUE },
        ].filter(d => d.value > 0);
    }, [summary]);

    // ── Export ────────────────────────────────────────────────────
    const handleExport = async () => {
        setIsExporting(true);
        try {
            const exportData = await learnerReportService.exportRecords({
                report_type: activeTab,
                search: debouncedSearch || undefined,
                timeline_status: timelineFilter || undefined,
                tracking_status: trackingFilter || undefined,
                urgency_levels: urgencyFilter ? [urgencyFilter] : undefined,
                mandatory_only: mandatoryFilter || undefined,
                _export_format: 'EXCEL',
            });

            const allRecords = exportData?.records || [];
            if (!allRecords.length) { toast.error('No data to export'); setIsExporting(false); return; }

            const wb = new ExcelJS.Workbook();
            wb.creator = 'Skillzen';
            wb.created = new Date();
            const s = exportData.summary;
            // Top 4 card-mapped totals always come from the unfiltered summary
            // endpoint so they match the UI cards (which ignore table filters).
            const unfiltered = summaryData?.summary;
            const completedCount = unfiltered?.completed_count ?? s.completed_count;
            const notStartedCount = unfiltered?.not_started_count ?? s.not_started_count;
            const inProgressCount = unfiltered?.in_progress_count ?? s.in_progress_count;
            const noDeadlineCount = unfiltered?.no_deadline_count ?? s.no_deadline_count;

            const PURPLE = '534AB7', RED = 'C4432B', BLUE = '2B7AB5', GREEN = '1D9E75';
            const styledHeader = (sheet: ExcelJS.Worksheet, color: string) => {
                const row = sheet.getRow(1);
                row.font = { bold: true, color: { argb: 'FFFFFF' }, size: 11 };
                row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
                row.alignment = { horizontal: 'center', wrapText: true };
            };

            // Shared column definitions (reused by Learner Records + Overdue Report sheets)
            const recordColumns: Partial<ExcelJS.Column>[] = [
                { header: '#', key: 'sno', width: 5 },
                { header: 'Learner Name', key: 'user_name', width: 25 },
                { header: 'Email', key: 'user_email', width: 28 },
                { header: 'Employee ID', key: 'employee_id', width: 14 },
                { header: 'Department', key: 'department', width: 20 },
                { header: 'Role', key: 'role', width: 18 },
                { header: 'Reporting Manager', key: 'rm_name', width: 22 },
                { header: 'Manager Email', key: 'rm_email', width: 28 },
                { header: 'HRBP', key: 'hrbp_name', width: 22 },
                { header: 'BU Head', key: 'bu_name', width: 22 },
                { header: 'Type', key: 'type', width: 16 },
                { header: 'Item Title', key: 'item_title', width: 35 },
                { header: 'Category', key: 'category', width: 18 },
                { header: 'Mandatory', key: 'mandatory', width: 11 },
                { header: 'Tracking Status', key: 'tracking_status', width: 16 },
                { header: 'Timeline Status', key: 'timeline_status', width: 16 },
                { header: 'Progress (%)', key: 'progress', width: 13 },
                { header: 'Assigned At', key: 'assigned', width: 14 },
                { header: 'Deadline', key: 'deadline', width: 14 },
                { header: 'Days Overdue', key: 'days_overdue', width: 13 },
                { header: 'Urgency', key: 'urgency', width: 11 },
                { header: 'Completed At', key: 'completed', width: 14 },
                { header: 'Last Accessed At', key: 'last_accessed', width: 14 },
                { header: 'Time Spent (HH:MM:SS)', key: 'time_spent', width: 18 },
                { header: 'Reminders', key: 'reminders', width: 11 },
            ];
            const colCount = recordColumns.length;

            // Shared row builder
            const buildRecordRow = (r: typeof allRecords[0], i: number) => ({
                sno: i + 1, user_name: r.user_name, user_email: r.user_email,
                employee_id: r.employee_id, department: r.department, role: r.role,
                rm_name: r.reporting_manager_name, rm_email: r.reporting_manager_email,
                hrbp_name: r.hrbp_name, bu_name: r.bu_head_name,
                type: TYPE_LABEL[r.item_type] || r.item_type, item_title: r.item_title,
                category: r.category_name || '',
                mandatory: r.is_mandatory ? 'Yes' : 'No',
                tracking_status: TRACKING_LABEL[r.tracking_status] || r.tracking_status,
                timeline_status: TIMELINE_LABEL[r.timeline_status] || r.timeline_status,
                progress: r.progress_percentage,
                assigned: formatDate(r.assigned_date, 'dd/mm/yyyy', 'N/A'),
                deadline: r.deadline_date ? formatDate(r.deadline_date, 'dd/mm/yyyy', 'N/A') : 'No deadline',
                days_overdue: r.days_overdue || '',
                urgency: r.timeline_status === 'OVERDUE' ? r.urgency_level : '',
                completed: r.completed_at ? formatDate(r.completed_at, 'dd/mm/yyyy', 'N/A') : '',
                last_accessed: r.last_accessed_at ? formatDate(r.last_accessed_at, 'dd/mm/yyyy', 'N/A') : 'Never',
                time_spent: formatMinutesAsTime(r.total_time_spent_minutes),
                reminders: r.reminder_count,
            });

            // Shared styling for urgency + mandatory cells
            const urgencyArgb: Record<string, string> = { CRITICAL: 'C4432B', HIGH: 'E06D44', MEDIUM: 'D4A843', LOW: '2B7AB5' };
            const styleRecordRows = (sheet: ExcelJS.Worksheet, records: typeof allRecords) => {
                records.forEach((r, i) => {
                    const row = sheet.getRow(i + 2);
                    if (r.timeline_status === 'OVERDUE') {
                        const uc = row.getCell('urgency');
                        const color = urgencyArgb[r.urgency_level];
                        if (color) {
                            uc.font = { bold: true, color: { argb: 'FFFFFF' } };
                            uc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
                        }
                    }
                    if (r.is_mandatory) row.getCell('mandatory').font = { bold: true, color: { argb: 'C4432B' } };
                });
            };

            // ── Sheet 1: Summary ──────────────────────────────
            const ss = wb.addWorksheet('Summary', { properties: { tabColor: { argb: PURPLE } } });
            ss.columns = [{ header: 'Metric', key: 'metric', width: 35 }, { header: 'Value', key: 'value', width: 20 }];
            const summaryRows: { metric: string; value: string | number }[] = [
                { metric: 'Report Generated', value: new Date().toLocaleString() },
                { metric: 'Report Type', value: activeTab },
                ...(debouncedSearch ? [{ metric: 'Search Filter', value: debouncedSearch }] : []),
                ...(timelineFilter ? [{ metric: 'Timeline Filter', value: TIMELINE_LABEL[timelineFilter] }] : []),
                ...(trackingFilter ? [{ metric: 'Tracking Filter', value: TRACKING_LABEL[trackingFilter] }] : []),
                ...(urgencyFilter ? [{ metric: 'Urgency Filter', value: urgencyFilter }] : []),
                ...(mandatoryFilter ? [{ metric: 'Mandatory Only', value: 'Yes' }] : []),
                { metric: '', value: '' },
                { metric: 'OVERVIEW', value: '' },
                { metric: 'Total Assignments', value: s.total_assignments },
                { metric: 'Unique Learners', value: s.unique_users },
                { metric: 'Unique Items', value: s.unique_items },
                { metric: 'Completion Rate (%)', value: `${s.completion_rate}%` },
                { metric: 'Mandatory (Total)', value: s.mandatory_total },
                { metric: 'Mandatory Overdue', value: s.mandatory_overdue },
                { metric: 'Avg. Progress (%)', value: `${s.avg_progress}%` },
                { metric: 'Avg. Time Spent (min)', value: s.avg_time_spent_minutes },
                { metric: '', value: '' },
                { metric: 'TIMELINE BREAKDOWN', value: '' },
                { metric: 'Completed', value: completedCount },
                { metric: 'Overdue', value: s.overdue_count },
                { metric: 'Due Soon (≤7 days)', value: s.due_soon_count },
                { metric: 'No Deadline', value: noDeadlineCount },
                { metric: '', value: '' },
                { metric: 'TRACKING STATUS', value: '' },
                { metric: 'Not Started', value: notStartedCount },
                { metric: 'In Progress', value: inProgressCount },
                { metric: 'Completed', value: completedCount },
                { metric: '', value: '' },
                { metric: 'URGENCY (overdue only)', value: '' },
                { metric: 'Critical (30+ days)', value: s.critical_count },
                { metric: 'High (14-29 days)', value: s.high_count },
                { metric: 'Medium (7-13 days)', value: s.medium_count },
                { metric: 'Low (1-6 days)', value: s.low_count },
            ];
            summaryRows.forEach(r => ss.addRow(r));
            styledHeader(ss, PURPLE);
            ss.eachRow((row) => {
                const val = row.getCell(1).value as string;
                if (val && ['OVERVIEW', 'TIMELINE BREAKDOWN', 'TRACKING STATUS', 'URGENCY (overdue only)'].includes(val)) {
                    row.font = { bold: true, color: { argb: PURPLE } };
                }
            });

            // ── Sheet 2: Learner Records (all records — purple theme) ──
            const ds = wb.addWorksheet('Learner Records', { properties: { tabColor: { argb: PURPLE } } });
            ds.columns = recordColumns;
            allRecords.forEach((r, i) => ds.addRow(buildRecordRow(r, i)));
            styledHeader(ds, PURPLE);
            ds.views = [{ state: 'frozen' as const, ySplit: 1 }];
            ds.autoFilter = { from: { row: 1, column: 1 }, to: { row: allRecords.length + 1, column: colCount } };
            styleRecordRows(ds, allRecords);

            // ── Sheet 3: Overdue Report (overdue records only — red theme) ──
            const overdueRecords = allRecords.filter(r => r.timeline_status === 'OVERDUE');
            const od = wb.addWorksheet('Overdue Report', { properties: { tabColor: { argb: RED } } });
            od.columns = recordColumns;
            overdueRecords.forEach((r, i) => od.addRow(buildRecordRow(r, i)));
            styledHeader(od, RED);
            od.views = [{ state: 'frozen' as const, ySplit: 1 }];
            if (overdueRecords.length > 0) {
                od.autoFilter = { from: { row: 1, column: 1 }, to: { row: overdueRecords.length + 1, column: colCount } };
            }
            styleRecordRows(od, overdueRecords);

            // ── Sheet 4: By Department ────────────────────────
            const dep = wb.addWorksheet('By Department', { properties: { tabColor: { argb: BLUE } } });
            dep.columns = [{ header: 'Department', key: 'department', width: 30 }, { header: 'Count', key: 'count', width: 18 }];
            (exportData.by_department || []).forEach(d => dep.addRow({ department: d.department, count: d.count }));
            styledHeader(dep, BLUE);

            // ── Sheet 5: By Item ──────────────────────────────
            const itm = wb.addWorksheet('By Item', { properties: { tabColor: { argb: GREEN } } });
            itm.columns = [
                { header: 'Item Title', key: 'item_title', width: 45 },
                { header: 'Type', key: 'type', width: 18 },
                { header: 'Count', key: 'count', width: 18 },
            ];
            (exportData.by_item || []).forEach(d => itm.addRow({ item_title: d.item_title, type: TYPE_LABEL[d.item_type] || d.item_type, count: d.count }));
            styledHeader(itm, GREEN);

            // ── Download ──────────────────────────────────────
            const tabNames: Record<string, string> = { ALL: 'all', COURSE: 'course', CERTIFICATION: 'certification', LEARNING_PATH: 'learning-path' };
            const parts = ['training-status-report', tabNames[activeTab] || activeTab.toLowerCase()];
            if (timelineFilter) parts.push(`timeline-${timelineFilter.toLowerCase()}`);
            if (trackingFilter) parts.push(`tracking-${trackingFilter.toLowerCase()}`);
            if (urgencyFilter) parts.push(`urgency-${urgencyFilter.toLowerCase()}`);
            if (mandatoryFilter) parts.push('mandatory');
            parts.push(new Date().toISOString().split('T')[0]);

            const buffer = await wb.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${parts.join('_')}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);

            toast.success(`Exported ${allRecords.length} records (${overdueRecords.length} overdue) — 5 sheets`);
        } catch (err: any) {
            toast.error(err?.message || 'Export failed');
        } finally {
            setIsExporting(false);
        }
    };

    // ── Loading / Error ──────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="rounded-xl p-6 text-white" style={{ background: `linear-gradient(135deg, ${PRIMARY_PURPLE} 0%, ${DARK_PURPLE} 100%)` }}>
                    <h1 className="text-2xl font-bold mb-2">Training Status Report</h1>
                </div>
                <MetricCardSkeleton count={5} />
                <MetricCardSkeleton count={4} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg border p-6 animate-pulse">
                        <SkeletonLoader type="title" className="mb-4" />
                        <SkeletonLoader type="custom" className="h-64 w-full rounded-lg" />
                    </div>
                    <div className="bg-white rounded-lg border p-6 animate-pulse">
                        <SkeletonLoader type="title" className="mb-4" />
                        <SkeletonLoader type="custom" className="h-64 w-full rounded-lg" />
                    </div>
                </div>
                <TableSkeleton rows={6} columns={8} />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="space-y-6">
                <div className="rounded-xl p-6 text-white" style={{ background: `linear-gradient(135deg, ${PRIMARY_PURPLE} 0%, ${DARK_PURPLE} 100%)` }}>
                    <h1 className="text-2xl font-bold mb-2">Training Status Report</h1>
                </div>
                <div className="card flex flex-col items-center justify-center py-16">
                    <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
                    <p className="text-lg font-medium text-gray-700">Failed to load report</p>
                    <button onClick={() => refetch()} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* ── Gradient Header ───────────────────────────────── */}
            <div className="rounded-xl p-6 text-white" style={{ background: `linear-gradient(135deg, ${PRIMARY_PURPLE} 0%, ${DARK_PURPLE} 100%)` }}>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold mb-2">Training Status Report</h1>
                        <p className="text-white/85">Complete tracking data — all learners, all statuses, all deadlines</p>
                        {summary && (
                            <div className="mt-3 flex flex-wrap gap-2">
                                <span className="text-xs px-3 py-1 rounded-full bg-white/20">Total: {summary.total_assignments}</span>
                                <span className="text-xs px-3 py-1 rounded-full bg-white/20">Completion: {summary.completion_rate}%</span>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors text-sm font-semibold border border-white/20 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={mandatoryFilter}
                                onChange={(e) => { setMandatoryFilter(e.target.checked); resetPage(); }}
                                className="rounded accent-indigo-600 focus:ring-indigo-500"
                            />
                            Mandatory only
                        </label>
                        <button
                            onClick={handleExport}
                            disabled={isExporting || !canWrite}
                            title={!canWrite ? 'You do not have permission to export' : undefined}
                            className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors text-sm font-semibold border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            Export Excel
                        </button>
                    </div>
                </div>

                {/* Item-type tabs */}
                <div className="mt-6 flex flex-wrap gap-2">
                    {([
                        { key: 'ALL' as LearnerReportType, label: 'All', icon: <Target className="w-4 h-4" /> },
                        { key: 'COURSE' as LearnerReportType, label: 'Courses', icon: <BookOpen className="w-4 h-4" /> },
                        { key: 'CERTIFICATION' as LearnerReportType, label: 'Certifications', icon: <Award className="w-4 h-4" /> },
                        { key: 'LEARNING_PATH' as LearnerReportType, label: 'Learning Paths', icon: <GraduationCap className="w-4 h-4" /> },
                    ]).map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => {
                                if (activeTab === tab.key) return;
                                setCardFilterPending(true);
                                setActiveTab(tab.key);
                                // Reset filters — they may not apply to the new item type
                                setTrackingFilter('');
                                setTimelineFilter('');
                                setUrgencyFilter('');
                                setSearchInput('');
                                setMandatoryFilter(false);
                                resetPage();
                            }}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === tab.key ? 'bg-white' : 'bg-white/20 text-white hover:bg-white/30'}`}
                            style={activeTab === tab.key ? { color: PRIMARY_PURPLE } : {}}
                        >
                            {tab.icon} {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Status Cards (4) — clickable, filter the table ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {([
                    { key: 'tracking:COMPLETED', label: 'Completed', count: statusCardSummary?.completed_count || 0, icon: <CheckCircle className="w-5 h-5" />, color: 'bg-emerald-100 text-emerald-700', isActive: trackingFilter === 'COMPLETED', apply: () => applyStatusCard('COMPLETED') },
                    { key: 'tracking:NOT_STARTED', label: 'Not Started', count: statusCardSummary?.not_started_count || 0, icon: <CircleDashed className="w-5 h-5" />, color: 'bg-red-100 text-red-700', isActive: trackingFilter === 'NOT_STARTED', apply: () => applyStatusCard('NOT_STARTED') },
                    { key: 'tracking:IN_PROGRESS', label: 'In Progress', count: statusCardSummary?.in_progress_count || 0, icon: <Hourglass className="w-5 h-5" />, color: 'bg-amber-100 text-amber-700', isActive: trackingFilter === 'IN_PROGRESS', apply: () => applyStatusCard('IN_PROGRESS') },
                    { key: 'timeline:NO_DEADLINE', label: 'No Deadline', count: statusCardSummary?.no_deadline_count || 0, icon: <CalendarOff className="w-5 h-5" />, color: 'bg-purple-100 text-purple-700', isActive: timelineFilter === 'NO_DEADLINE', apply: () => applyNoDeadlineCard() },
                ]).map(card => (
                    <div
                        key={card.key}
                        onClick={() => card.apply()}
                        className={`card card-hover cursor-pointer transition ${card.isActive ? 'ring-2 ring-indigo-500' : ''}`}
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className={`p-2 rounded-lg ${card.color}`}>{card.icon}</div>
                            {card.isActive && <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">Active</span>}
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{card.count}</p>
                        <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">{card.label}</p>
                    </div>
                ))}
            </div>

            {/* ── Urgency Cards (4) — clickable, filter overdue records ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {([
                    { level: 'CRITICAL' as UrgencyLevel, label: 'Critical', sub: '30+ days overdue', count: baseSummary?.critical_count || 0, color: 'bg-red-100 text-red-700' },
                    { level: 'HIGH' as UrgencyLevel, label: 'High', sub: '14-29 days', count: baseSummary?.high_count || 0, color: 'bg-orange-100 text-orange-700' },
                    { level: 'MEDIUM' as UrgencyLevel, label: 'Medium', sub: '7-13 days', count: baseSummary?.medium_count || 0, color: 'bg-amber-100 text-amber-700' },
                    { level: 'LOW' as UrgencyLevel, label: 'Low', sub: '1-6 days', count: baseSummary?.low_count || 0, color: 'bg-blue-100 text-blue-700' },
                ]).map(card => (
                    <div
                        key={card.level}
                        onClick={() => applyUrgencyCard(card.level)}
                        className={`card card-hover cursor-pointer transition ${urgencyFilter === card.level ? 'ring-2 ring-indigo-500' : ''}`}
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className={`p-2 rounded-lg ${card.color}`}><AlertTriangle className="w-5 h-5" /></div>
                            {urgencyFilter === card.level && <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">Active</span>}
                        </div>
                        <p className="text-3xl font-bold text-gray-900">{card.count}</p>
                        <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">{card.label}</p>
                        <p className="text-xs text-gray-400">{card.sub}</p>
                    </div>
                ))}
            </div>

            {/* ── Key Metrics (6) — display only ─────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {([
                    { title: 'Total Assignments', value: summary?.total_assignments || 0, icon: <Target className="w-5 h-5" />, color: 'bg-indigo-100 text-indigo-700' },
                    { title: 'Unique Learners', value: summary?.unique_users || 0, icon: <Users className="w-5 h-5" />, color: 'bg-purple-100 text-purple-700' },
                    { title: 'Unique Items', value: summary?.unique_items || 0, icon: <BookOpen className="w-5 h-5" />, color: 'bg-blue-100 text-blue-700' },
                    { title: 'Completion Rate', value: `${summary?.completion_rate || 0}%`, icon: <CheckCircle className="w-5 h-5" />, color: 'bg-emerald-100 text-emerald-700' },
                    { title: 'Mandatory Overdue', value: summary?.mandatory_overdue || 0, icon: <Shield className="w-5 h-5" />, color: 'bg-red-100 text-red-700' },
                    { title: 'Avg. Progress', value: `${summary?.avg_progress || 0}%`, icon: <TrendingUp className="w-5 h-5" />, color: 'bg-amber-100 text-amber-700' },
                ]).map((m, i) => (
                    <div key={i} className="card card-hover">
                        <div className="flex items-start justify-between mb-3"><div className={`p-2 rounded-lg ${m.color}`}>{m.icon}</div></div>
                        <p className="text-xl font-bold text-gray-900">{m.value}</p>
                        <p className="text-xs text-gray-500 mt-1">{m.title}</p>
                    </div>
                ))}
            </div>

            {/* ── Charts: Urgency Breakdown + Tracking Distribution ─ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="card">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Urgency Breakdown</h2>
                    {summary && (summary.critical_count + summary.high_count + summary.medium_count + summary.low_count) > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart
                                data={[
                                    { name: 'Critical', count: summary?.critical_count || 0, color: URGENCY_COLOR.CRITICAL, sub: '30+ days' },
                                    { name: 'High', count: summary?.high_count || 0, color: URGENCY_COLOR.HIGH, sub: '14-29 days' },
                                    { name: 'Medium', count: summary?.medium_count || 0, color: URGENCY_COLOR.MEDIUM, sub: '7-13 days' },
                                    { name: 'Low', count: summary?.low_count || 0, color: URGENCY_COLOR.LOW, sub: '1-6 days' },
                                ]}
                                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 12 }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                    {[URGENCY_COLOR.CRITICAL, URGENCY_COLOR.HIGH, URGENCY_COLOR.MEDIUM, URGENCY_COLOR.LOW].map((c, i) => (
                                        <Cell key={i} fill={c} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : <EmptyChart />}
                </div>
                <div className="card">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Tracking Status Distribution</h2>
                    {trackingChartData.length ? (
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie data={trackingChartData} cx="50%" cy="50%" outerRadius={95} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                                    {trackingChartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : <EmptyChart />}
                </div>
            </div>

            {/* ── Breakdowns: By Department + By Item (both horizontal bars) ─ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="card">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">By Department</h2>
                        <span className="text-sm text-gray-500">
                            {byDepartment.length > 10 ? `Top 10 of ${byDepartment.length}` : `${byDepartment.length} dept${byDepartment.length !== 1 ? 's' : ''}`}
                        </span>
                    </div>
                    {byDepartment.length ? (
                        <ResponsiveContainer width="100%" height={Math.max(220, Math.min(byDepartment.length, 10) * 38)}>
                            <BarChart data={byDepartment.slice(0, 10)} layout="vertical" margin={{ top: 5, right: 40, left: 10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis type="number" tick={{ fontSize: 12 }} />
                                <YAxis type="category" dataKey="department" tick={{ fontSize: 12 }} width={140} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="count" fill={PRIMARY_PURPLE} radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : <EmptyChart />}
                </div>
                <div className="card">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">By Item</h2>
                        <span className="text-sm text-gray-500">
                            {byItem.length > 10 ? `Top 10 of ${byItem.length}` : `${byItem.length} item${byItem.length !== 1 ? 's' : ''}`}
                        </span>
                    </div>
                    {byItem.length ? (
                        <ResponsiveContainer width="100%" height={Math.max(220, Math.min(byItem.length, 10) * 38)}>
                            <BarChart data={byItem.slice(0, 10).map(d => ({ ...d, label: d.item_title.length > 28 ? d.item_title.slice(0, 28) + '…' : d.item_title }))} layout="vertical" margin={{ top: 5, right: 40, left: 10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis type="number" tick={{ fontSize: 12 }} />
                                <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={180} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="count" fill={SUCCESS_GREEN} radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : <EmptyChart />}
                </div>
            </div>

            {/* ── Modal: full text for truncated Department / Item cells ── */}
            <Modal
                isOpen={!!cellDetailModal}
                onClose={() => setCellDetailModal(null)}
                title={cellDetailModal?.title || ''}
                size="md"
            >
                <p className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">
                    {cellDetailModal?.content}
                </p>
            </Modal>

            {/* ── Filter Bar (search + mandatory + page size + clear) ── */}
            <div className="card">
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        type="text"
                        placeholder="Search by name, email, employee ID, item title, or department..."
                        value={searchInput}
                        onChange={(e) => { setSearchInput(e.target.value); resetPage(); }}
                        className="flex-1 min-w-[240px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    {(trackingFilter || timelineFilter || urgencyFilter) && (
                        <button
                            onClick={clearCardFilters}
                            className="px-3 py-2 rounded-lg text-sm text-indigo-600 hover:bg-indigo-50 font-medium"
                        >
                            Clear card filter
                        </button>
                    )}
                </div>
            </div>

            {/* ── Records table — clean reference styling ─────── */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                {isTableRefreshing ? (
                    <div className="p-4">
                        <TableSkeleton rows={pageSize} columns={10} />
                    </div>
                ) : (
                <div className="overflow-x-auto" style={{ maxHeight: '600px' }}>
                    <table className="min-w-full" style={{ borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ background: '#f8f8f8' }} className="sticky top-0 z-10">
                                {[
                                    { label: '#', sortKey: null, w: 40 },
                                    { label: 'Learner', sortKey: 'user_name' as const, w: 200 },
                                    { label: 'Emp ID', sortKey: null, w: 90 },
                                    { label: 'Department', sortKey: null, w: 160 },
                                    { label: 'Reporting Manager', sortKey: null, w: 150 },
                                    { label: 'HRBP', sortKey: null, w: 130 },
                                    { label: 'BU Head', sortKey: null, w: 130 },
                                    { label: 'Item', sortKey: null, w: 220 },
                                    { label: 'Category', sortKey: null, w: 130 },
                                    { label: 'Type', sortKey: null, w: 95 },
                                    { label: 'Status', sortKey: null, w: 110 },
                                    { label: 'Progress', sortKey: 'progress_percentage' as const, w: 130 },
                                    { label: 'Assigned At', sortKey: null, w: 110 },
                                    { label: 'Deadline', sortKey: 'deadline_date' as const, w: 100 },
                                    { label: 'Days Over', sortKey: 'days_overdue' as const, w: 80 },
                                    { label: 'Timeline Status', sortKey: null, w: 120 },
                                    { label: 'Urgency', sortKey: null, w: 100 },
                                    { label: 'Completed At', sortKey: 'completed_at' as const, w: 110 },
                                    { label: 'Last Accessed At', sortKey: null, w: 130 },
                                    { label: 'Time Spent', sortKey: null, w: 90 },
                                    { label: 'Reminders', sortKey: null, w: 80 },
                                ].map((col, i) => (
                                    <th
                                        key={i}
                                        onClick={col.sortKey ? () => handleSort(col.sortKey!) : undefined}
                                        style={{
                                            padding: '10px 8px',
                                            fontWeight: 600,
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px',
                                            color: '#666',
                                            textAlign: 'left',
                                            whiteSpace: 'nowrap',
                                            cursor: col.sortKey ? 'pointer' : 'default',
                                            minWidth: col.w,
                                        }}
                                    >
                                        {col.sortKey ? (
                                            <div className="flex items-center gap-1">{col.label} <SortIcon field={col.sortKey} /></div>
                                        ) : col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRecords.length === 0 ? (
                                <tr>
                                    <td colSpan={21} style={{ padding: '48px 12px', textAlign: 'center', color: '#aaa' }}>
                                        <CircleDashed className="w-10 h-10 mx-auto mb-2 opacity-40" />
                                        <div>No records match the current filters.</div>
                                    </td>
                                </tr>
                            ) : sortedRecords.map((r, i) => (
                                <tr
                                    key={`${r.user_id}-${r.item_id}-${i}`}
                                    style={{ borderTop: '1px solid #f0f0f0' }}
                                    className="hover:bg-gray-50 transition-colors"
                                >
                                    <td style={cellStyle({ color: '#999' })}>{(pagination ? (pagination.page - 1) * pagination.limit : 0) + i + 1}</td>
                                    <td style={cellStyle()}>
                                        <div style={{ fontWeight: 600, color: '#1d1d1f' }}>{r.user_name}</div>
                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{r.user_email}</div>
                                    </td>
                                    <td style={cellStyle({ color: '#444' })}>{r.employee_id || '—'}</td>
                                    <td style={cellStyle({ color: '#444', maxWidth: 160 })}>
                                        <div className="flex items-center gap-1">
                                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{r.department}</div>
                                            <button
                                                onClick={() => setCellDetailModal({ title: 'Department', content: r.department })}
                                                className="p-1 text-gray-400 hover:text-indigo-600 transition-colors flex-shrink-0"
                                                title="View full department"
                                            >
                                                <ExternalLink className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                    <td style={cellStyle({ color: '#444' })} title={r.reporting_manager_email}>{r.reporting_manager_name}</td>
                                    <td style={cellStyle({ color: '#444' })} title={r.hrbp_email}>{r.hrbp_name}</td>
                                    <td style={cellStyle({ color: '#444' })} title={r.bu_head_email}>{r.bu_head_name}</td>
                                    <td style={cellStyle({ maxWidth: 220 })}>
                                        <div className="flex items-center gap-1">
                                            <div style={{ color: '#1d1d1f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{r.item_title}</div>
                                            <button
                                                onClick={() => setCellDetailModal({ title: 'Item', content: r.item_title })}
                                                className="p-1 text-gray-400 hover:text-indigo-600 transition-colors flex-shrink-0"
                                                title="View full item title"
                                            >
                                                <ExternalLink className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                    <td style={cellStyle({ color: '#444', fontSize: '12px' })}>{r.category_name || '—'}</td>
                                    <td style={cellStyle()}>
                                        <Tag bg={r.is_mandatory ? '#fde8e8' : '#e8f5e9'} color={r.is_mandatory ? '#C4432B' : '#1D9E75'}>
                                            {r.is_mandatory ? 'Mandatory' : 'Optional'}
                                        </Tag>
                                    </td>
                                    <td style={cellStyle()}><StatusTag status={r.tracking_status} /></td>
                                    <td style={cellStyle()}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <div style={{ width: 80, height: 6, background: '#e0e0e0', borderRadius: 3, display: 'inline-block' }}>
                                                <div style={{ width: `${r.progress_percentage}%`, height: '100%', background: PRIMARY_PURPLE, borderRadius: 3 }} />
                                            </div>
                                            <span style={{ fontSize: '11px', color: '#666' }}>{r.progress_percentage}%</span>
                                        </div>
                                    </td>
                                    <td style={cellStyle({ color: '#444', fontSize: '12px' })}>{formatDate(r.assigned_date, 'dd/mm/yyyy', '—')}</td>
                                    <td style={cellStyle({ color: '#444', fontSize: '12px' })}>{r.deadline_date ? formatDate(r.deadline_date, 'dd/mm/yyyy', '—') : '—'}</td>
                                    <td style={cellStyle({ fontSize: '12px', fontWeight: r.days_overdue > 0 ? 600 : 400, color: r.days_overdue > 0 ? '#C4432B' : '#444' })}>
                                        {r.days_overdue || '—'}
                                    </td>
                                    <td style={cellStyle()}><TimelineTag status={r.timeline_status} /></td>
                                    <td style={cellStyle()}>
                                        {r.timeline_status === 'OVERDUE' ? (
                                            <span style={{
                                                background: URGENCY_COLOR[r.urgency_level],
                                                color: '#fff',
                                                padding: '2px 8px',
                                                borderRadius: 4,
                                                fontSize: 11,
                                                fontWeight: 700,
                                                display: 'inline-block',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px',
                                            }}>
                                                {r.urgency_level}
                                            </span>
                                        ) : <span style={{ color: '#bbb' }}>—</span>}
                                    </td>
                                    <td style={cellStyle({ color: '#444', fontSize: '12px' })}>{r.completed_at ? formatDate(r.completed_at, 'dd/mm/yyyy', '—') : '—'}</td>
                                    <td style={cellStyle({ color: '#444', fontSize: '12px' })}>{r.last_accessed_at ? formatDate(r.last_accessed_at, 'dd/mm/yyyy', '—') : 'Never'}</td>
                                    <td style={cellStyle({ color: '#444', fontSize: '12px', fontFamily: 'monospace' })}>{formatMinutesAsTime(r.total_time_spent_minutes)}</td>
                                    <td style={cellStyle({ color: '#444', fontSize: '12px' })}>{r.reminder_count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                )}
                {!isTableRefreshing && pagination && pagination.total > 0 && (
                    <div className="border-t border-gray-100 p-3">
                        <Pagination
                            currentPage={pagination.page}
                            totalPages={pagination.total_pages}
                            onPageChange={(p) => setPage(p)}
                            totalItems={pagination.total}
                            itemsPerPage={pagination.limit}
                            onItemsPerPageChange={(newPageSize) => {
                                setPageSize(newPageSize);
                                resetPage();
                            }}
                            showItemsPerPage={true}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Tiny helpers for the table (match reference HTML styling) ──

function EmptyChart() {
    return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <CircleDashed className="w-10 h-10 mb-2 opacity-40" />
            <p className="text-sm">No data available</p>
        </div>
    );
}

function cellStyle(extra: React.CSSProperties = {}): React.CSSProperties {
    return {
        padding: '8px',
        verticalAlign: 'top',
        fontSize: '13px',
        ...extra,
    };
}

function Tag({ bg, color, children }: { bg: string; color: string; children: React.ReactNode }) {
    return (
        <span
            style={{
                background: bg,
                color,
                padding: '2px 8px',
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 600,
                display: 'inline-block',
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </span>
    );
}

function StatusTag({ status }: { status: LearnerTrackingStatus }) {
    const map: Record<LearnerTrackingStatus, { bg: string; color: string; label: string }> = {
        COMPLETED: { bg: '#e8f5e9', color: '#1D9E75', label: 'Completed' },
        IN_PROGRESS: { bg: '#fff3e0', color: '#b8860b', label: 'In Progress' },
        NOT_STARTED: { bg: '#fde8e8', color: '#C4432B', label: 'Not Started' },
    };
    const m = map[status];
    return <Tag bg={m.bg} color={m.color}>{m.label}</Tag>;
}

function TimelineTag({ status }: { status: TimelineStatus }) {
    const map: Record<TimelineStatus, { bg: string; color: string; label: string }> = {
        COMPLETED: { bg: '#e8f5e9', color: '#1D9E75', label: 'Completed' },
        OVERDUE: { bg: '#fde8e8', color: '#C4432B', label: 'Overdue' },
        DUE_SOON: { bg: '#fff3e0', color: '#b8860b', label: 'Due Soon' },
        ON_TRACK: { bg: '#e3f2fd', color: '#2B7AB5', label: 'On Track' },
        NO_DEADLINE: { bg: '#f3e8ff', color: '#7c3aed', label: 'No Deadline' },
    };
    const m = map[status];
    return <Tag bg={m.bg} color={m.color}>{m.label}</Tag>;
}

